// modules
import {
  BeaconAtomFamily,
  FileChangedGroupIdsAtom,
  MarkerAtomFamily,
  PoiAtomFamily
} from 'modules';

// enums
import { RiskAssessmentTypeName } from 'enums';

// libs
import { formatBuildingData, formattedRiskAssessment } from 'libs';
import { getBeaconBoundaryByUnit, getUnitScale, meterToDrawingUnit } from 'libs/drawingUnit.js';

// libraries
import _ from 'lodash-es';
import { Vector3 } from 'three';
import { v4 as uuid } from 'uuid';
import { atom, selector } from 'recoil';
import dayjs from 'dayjs';
import { ALL_HIDDEN, MAIN_OBJECT } from 'variables';
import { DATE_FORMAT } from 'variables/consts.js';

/**
 * ParentDataAtom - 부모로 부터 전달받은 데이터
 *
 * pageId : 페이지 ID
 * messageType : 메시지 타입 (default: undefined)
 * - initPage : 초기화
 * - getInfo : 저장을 위한 정보요청
 * company : 회사정보
 * project : 프로젝트 정보
 * modeling : 모델링 정보
 * purpose : 위험성평가 정보
 * singlePictureUrls : 단일 이미지 URL
 * pairPictureUrl : 조치전후 이미지 URL
 * markers : 마커 정보
 * pois : POI 정보
 */
export const ParentDataAtom = atom({
  key: 'ParentDataAtom',
  default: undefined
});

/**
 * ParentModeAtom - 부모로 부터 전달받은 모드
 * default: undefined
 * - register : 등록
 * - retrieve : 수정(?) 및 보기
 */
export const ParentModeAtom = atom({
  key: 'ParentModeAtom',
  default: undefined
});

/**
 * ParentMetaDataAtom - 부모로 부터 전달받은 메타 데이터
 *
 * pageId: 페이지 ID
 * messageType: 메시지 타입 (default: undefined)
 * - initPage : 초기화
 * - getInfo : 저장을 위한 정보요청
 */
export const ParentMetaDataAtom = atom({
  key: 'ParanetMetaDataAtom',
  default: undefined
});

/**
 * ParentDataSettingAtom - 부모로 부터 전달받은 데이터 셋팅 완료 여부
 */
export const ParentDataSettingAtom = atom({
  key: 'ParentDataSettingAtom',
  default: false
});

/**
 * SiteInformationAtom - 현장 정보
 */
export const CurrentSiteInformationAtom = atom({
  key: 'CurrentSiteInformationAtom',
  default: undefined
});

/**
 * CameraControllerTypeAtom - 카메라 컨트롤러 타입
 * dollhouse : 전체 보기(기본)
 * floor : 평면도 보기
 * onePerson : 1인칭
 * threePerson : 3인칭
 */
export const CameraControllerTypeAtom = atom({
  key: 'CameraControllerTypeAtom',
  default: 'dollhouse'
});

/**
 * CoordinateAtom - 현재 좌표
 */
export const CoordinateAtom = atom({
  key: 'CoordinateAtom',
  default: {
    x: 0,
    y: 0,
    z: 0
  }
});

/**
 * LoadModelUrlAtom - 모델 URL
 */
export const LoadModelUrlAtom = atom({
  key: 'LoadModelUrlAtom',
  default: undefined
});

/**
 * SiteInformationDataAtom - 현장 정보 데이터
 * site: 현장
 * building: 동
 * floor: 층
 */
export const SiteInformationDataAtom = atom({
  key: 'SiteInformationDataAtom',
  default: {
    site: undefined,
    building: undefined,
    floor: undefined
  }
});

/**
 * ModelBuildingListAtom - 모델링에서 가져온 동 정보
 */
export const ModelBuildingListAtom = atom({
  key: 'ModelBuildingListAtom',
  default: []
});

/**
 * 모델링에서 가져온 층 정보
 */
export const ModelFloorListAtom = atom({
  key: 'ModelFloorListAtom',
  default: []
});

/**
 * ModelBuildingInfoListAtom - 모델링에서 가져온 동 정보 리스트
 */
export const ModelBuildingInfoListAtom = atom({
  key: 'ModelBuildingInfoListAtom',
  default: undefined
});

/**
 * ParentModelBuildingInfoListInitAtom - 부모로 부터 전달받은 모델링 동 정보 리스트 초기화 여부
 */
export const ParentModelBuildingInfoListInitAtom = atom({
  key: 'ParentModelBuildingInfoListInitAtom',
  default: false
});

/**
 * ParentModelBuildingInfoListAtom - 부모로 부터 전달받은 모델링 동 정보 리스트
 */
export const ParentModelBuildingInfoListAtom = selector({
  key: 'ParentModelBuildingInfoListAtom',
  get: ({ get }) => {
    const parentData = get(ParentDataAtom);
    const parentModelBuildingInfoListInit = get(ParentModelBuildingInfoListInitAtom);
    const currentSiteInfo = get(CurrentSiteInformationAtom);

    if (parentModelBuildingInfoListInit) return undefined;
    if (!parentData?.modeling) return undefined;

    const { buildings, maxBasement } = parentData.modeling;

    const isSpecialSite = currentSiteInfo === 'S001' || currentSiteInfo === 'S002';

    /**
     * ---------------------------
     * 1️⃣ maxBasement-only mode
     * ---------------------------
     */
    if (maxBasement && (!buildings || buildings.length === 0)) {
      if (isSpecialSite) {
        // D0000만 존재
        return [
          {
            code: 'D0000',
            currentFloor: maxBasement
          }
        ];
      }

      // 일반 사이트
      return [
        {
          code: 'Basement',
          currentFloor: maxBasement
        },
        {
          code: 'Ground',
          currentFloor: ALL_HIDDEN
        }
      ];
    }

    /**
     * ---------------------------
     * 2️⃣ buildings 존재
     * ---------------------------
     */
    if (buildings && buildings.length > 0) {
      const formattedBuildings = formatBuildingData(buildings, currentSiteInfo);

      const buildingList = formattedBuildings.map((building) => ({
        code: building.code,
        currentFloor: building.currentFloor
      }));

      if (isSpecialSite) {
        return [
          ...buildingList,
          {
            code: 'D0000',
            currentFloor: MAIN_OBJECT
          }
        ];
      }

      return [
        ...buildingList,
        {
          code: 'Basement',
          currentFloor: MAIN_OBJECT
        },
        {
          code: 'Ground',
          currentFloor: MAIN_OBJECT
        }
      ];
    }

    return undefined;
  }
});

/**
 * MapObjectFloorListsAtom - 맵 객체 층별 리스트
 */
export const MapObjectFloorListsAtom = atom({
  key: 'MapObjectFloorListsAtom',
  default: undefined
});

/**
 * FloorOrderConfigAtom - 층 순서 설정 (floorOrderJson)
 */
export const FloorOrderConfigAtom = atom({
  key: 'FloorOrderConfigAtom',
  default: null
});

/**
 * MapObjectStructureListAtom - 맵 객체 구조 리스트
 */
export const MapObjectStructureListAtom = atom({
  key: 'MapObjectStructureListAtom',
  default: []
});

/**
 * PlayerDataAtom - 플레이어 데이터
 */
export const PlayerDataAtom = atom({
  key: 'PlayerDataAtom',
  default: {
    id: uuid(),
    position: new Vector3(-2.338060651447524, -1.1999999284744032, -48.28840391604004)
  }
});

/**
 * PlayerAlramTextAtim - 플레이어 알람 텍스트
 */
export const PlayerAlramTextAtom = atom({
  key: 'PlayerAlramTextAtom',
  default: '🚨 안전조치 필수!'
});

/**
 * RiskAssessmentListAtom - 위험성평가 리스트
 */
export const RiskAssessmentListAtom = selector({
  key: 'RiskAssessmentListAtom',
  get: ({ get }) => {
    const parentData = get(ParentDataAtom);
    const parentMode = get(ParentModeAtom);

    if (parentData && parentMode && parentData.markerGroups && parentData.markerGroups.length > 0) {
      const newResult = parentData.markerGroups.map((item) => {
        return {
          ...item,
          originData: parentData,
          purpose: {
            ...item.purpose,
            header: item.purpose.header,
            bodies: item.purpose.bodies.map((body) => ({
              ...body,
              key: RiskAssessmentTypeName[body.title]
            }))
          }
        };
      });

      return newResult;
    }

    return [];
  }
});

/**
 * SelectedRiskAssessmentIdAtom - 선택된 위험성평가 ID
 */
export const SelectedRiskAssessmentIdAtom = atom({
  key: 'SelectedRiskAssessmentIdAtom',
  default: undefined
});

/**
 * RiskAssessmentDataAtom - 위험성평가 데이터
 */
export const RiskAssessmentDataAtom = selector({
  key: 'RiskAssessmentDataAtom',
  get: ({ get }) => {
    const parentData = get(ParentDataAtom);
    const parentMode = get(ParentModeAtom);
    const riskAssessmentList = get(RiskAssessmentListAtom);
    const selectedRiskAssessmentId = get(SelectedRiskAssessmentIdAtom);

    const failResult = {
      title: undefined,
      purpose: [],
      riskImageType: undefined,
      riskSigleImageList: [],
      riskPairImageList: {
        lefts: [],
        rights: []
      },
      originData: undefined
    };

    if (!parentMode || !parentData) {
      return failResult;
    }

    const findItem =
      selectedRiskAssessmentId &&
      riskAssessmentList &&
      riskAssessmentList.length > 0 &&
      riskAssessmentList.find((item) => item.id === selectedRiskAssessmentId);

    const title = findItem ? findItem?.purpose?.header : parentData?.purpose?.header;
    const bodies = findItem ? findItem?.purpose?.bodies : parentData?.purpose?.bodies;

    if (!bodies) {
      return failResult;
    }

    const result = bodies.map((body) => ({
      key: uuid(),
      label: body.title,
      children: body.content,
      span: 3
    }));

    const formatingData = findItem ? findItem : parentData;
    const formattedImages = formattedRiskAssessment(formatingData, parentMode);

    const successResult = {
      ...formattedImages,
      title,
      purpose: result,
      originId: formatingData.id,
      originData: parentData
    };

    return successResult;
  }
});

/** 위험성평가 getInfo 보낼 데이터 */
export const RiskAssessmentGetInfoDataAtom = selector({
  key: 'RiskAssessmentGetInfoDataAtom',
  get: ({ get }) => {
    const parentData = get(ParentDataAtom);
    const markerList = get(MarkerAtomFamily('all'));
    const poiList = get(PoiAtomFamily('all'));
    const riskAssessmentList = get(RiskAssessmentListAtom);
    const fileChangedGroupIds = get(FileChangedGroupIdsAtom);

    const markerAndPoiList = [...markerList, ...poiList];

    const groupMarkers = _.groupBy(markerAndPoiList, 'data.originId');
    const groupMarkersValues = Object.values(groupMarkers);

    const riskAssessmentStatus = riskAssessmentList.map((riskItem) => {
      const exists = markerAndPoiList.some((marker) => marker.originId === riskItem.id);

      return {
        id: riskItem.id,
        exists: exists
      };
    });

    console.log('=== Risk Assessment Status === : ', riskAssessmentStatus);

    const newMarkerGroups = _.map(groupMarkersValues, (item) => {
      const markers = item.filter((marker) => marker.type === 'marker');
      const pois = item.filter((poi) => poi.type === 'poi');

      const groupId = item[0].data.originId;
      const parentGroup = parentData?.markerGroups?.find((g) => g.id === groupId);

      return {
        id: groupId,
        ...(parentGroup?.fileKey && { fileKey: parentGroup.fileKey }),
        isFileChanged: fileChangedGroupIds.includes(groupId),
        markers:
          markers && markers.length > 0
            ? markers.map((item) => ({
                lat: '',
                lng: '',
                x: item.position[0],
                y: item.position[1],
                z: item.position[2],
                key: `${item.objectName}-${item.selectedFloor}`,
                label: item.title,
                camera: item.camera,
                type: item.structureOptions
              }))
            : [],
        pois:
          pois && pois.length > 0
            ? pois.map((item) => ({
                lat: '',
                lng: '',
                x: item.position[0],
                y: item.position[1],
                z: item.position[2],
                key: item.objectName,
                label: item.title,
                type: item.structureOptions,
                camera: item.camera,
                scale: item.scale,
                rotation: item.rotation
              }))
            : []
      };
    });

    riskAssessmentList.forEach((riskItem) => {
      if (!newMarkerGroups.some((group) => group.id === riskItem.id)) {
        newMarkerGroups.push({
          id: riskItem.id,
          isFileChanged: fileChangedGroupIds.includes(riskItem.id),
          fileKey: riskItem.fileKey,
          markers: [],
          pois: []
        });
      }
    });

    return {
      pageId: parentData.pageId,
      messageType: parentData.messageType,
      markerGroups: newMarkerGroups
    };
  }
});

/** 비콘 getInfo data */
export const BeaconGetInfoDataAtom = selector({
  key: 'BeaconGetInfoDataAtom',
  get: ({ get }) => {
    const parentData = get(ParentDataAtom);
    const beaconList = get(BeaconAtomFamily('all'));

    const formattedBeacons = beaconList.map((beacon) => {
      const position = beacon.position;

      if (!position) {
        return {
          id: beacon.id,
          location: beacon.location ?? '',
          beacon: null
        };
      }
      const x = position[0] ?? beacon.x ?? 0;
      const y = position[1] ?? beacon.y ?? 0;
      const z = position[2] ?? beacon.z ?? 0;

      const camera = beacon.camera || {
        x: 0,
        y: 0,
        z: 0,
        cx: 0,
        cy: 0,
        cz: 0
      };

      const type =
        beacon.structureOptions && Object.keys(beacon.structureOptions).length > 0
          ? beacon.structureOptions
          : null;

      return {
        id: beacon.id,
        location: beacon.location ?? '',
        beacon: {
          lat: beacon.lat ?? '',
          lng: beacon.lng ?? '',
          x,
          y,
          z,
          key: beacon.key ?? '',
          camera,
          type
        }
      };
    });

    return {
      pageId: parentData.pageId,
      beaconGroups: formattedBeacons
    };
  }
});

/** 위험성평가 타임라인 날짜 */
export const RiskAssessmentDateDataAtom = atom({
  key: 'RiskAssessmentDateDataAtom',
  default: {
    startDate: dayjs().startOf('month').format(DATE_FORMAT),
    endDate: dayjs().endOf('month').format(DATE_FORMAT),
    focusedDate: dayjs().format(DATE_FORMAT)
  }
});

/**
 * DrawingUnitsAtom - 모델링 유닛 단위
 */
export const DrawingUnitsAtom = atom({
  key: 'DrawingUnitsAtom',
  default: undefined
});

/**
 * DrawingScaleSelector - 드로잉 스케일 값
 */
export const DrawingScaleSelector = selector({
  key: 'DrawingScaleSelector',
  get: ({ get }) => {
    const drawingUnits = get(DrawingUnitsAtom);
    return getUnitScale(drawingUnits, 2);
  }
});

/**
 * DrawingPoiScaleSelector - POI 스케일 값
 */
export const DrawingPoiScaleSelector = selector({
  key: 'DrawingPoiScaleSelector',
  get: ({ get }) => {
    const drawingUnits = get(DrawingUnitsAtom);
    return getUnitScale(drawingUnits, 0.2);
  }
});

/**
 * DrawingFloorOffsetYSelector - 바닥으로부터 Y축 오프셋 값
 */
export const DrawingFloorOffsetYSelector = selector({
  key: 'DrawingFloorOffsetYSelector',
  get: ({ get }) => {
    const drawingUnits = get(DrawingUnitsAtom);
    return meterToDrawingUnit(drawingUnits, 1);
  }
});

/**
 * DrawingBeaconBoundarySelector - 비콘 경계 영역 값
 */
export const DrawingBeaconBoundarySelector = selector({
  key: 'DrawingBeaconBoundarySelector',
  get: ({ get }) => {
    const drawingUnits = get(DrawingUnitsAtom);
    return getBeaconBoundaryByUnit(drawingUnits, 3.5, 1);
  }
});

/**
 * PoiActiveIndicatorScale - 해당하는 POI 구체 스케일
 */
export const PoiActiveIndicatorScale = selector({
  key: 'PoiActiveIndicatorScale',
  get: ({ get }) => {
    const drawingUnits = get(DrawingUnitsAtom);
    return getUnitScale(drawingUnits, 70);
  }
});

/**
 * 화면캡쳐 가능 여부
 */
export const CanCaptureSelector = selector({
  key: 'CanCaptureSelector',
  get: ({ get }) => {
    const parentData = get(ParentDataAtom);
    return !!parentData?.company?.scopeKey && !!parentData?.project?.code;
  }
});
